<template>
  <div class="textual_menu--container page-content">
    <h1 ref="title">
      <span v-if="menu">{{ menu.full_title }} </span>
    </h1>

    <menu-switcher :menus="allRelatedMenus"
                   :current-menu-id="currentMenuId"
                   :device-asset-code="deviceAssetCode"
                   is-dock-left
                   ref="switcher" @opened="closeSearchMenu">
    </menu-switcher>

    <search-menu @view-menu-item="scrollToMenuItem($event)"
                 @toggle-menu-item-favorite="$store.commit('toggleMenuItemSelected', [$event, menu?.menu_id || 0])"
                 :search-index="searchIndex"
                 :search-key="searchKey"
                 :search-host="searchHost"
                 :menu="menu"
                 :is-cart-enabled="isCartEnabled"
                 minimize-widget ref="search"></search-menu>

    <a v-if="isCartEnabled"
       class="item_button--container item_button--container-cart material-icons textual_menu--glass_effect"
       @click.prevent="showSelectedMenuItems = !showSelectedMenuItems" href="#"
       :class="{'item_button--container-active': showSelectedMenuItems}" title="Click to view your saved items"></a>

    <div v-if="menu && menu.menu_content_area_top.length"
         class="body-container page-links no-images show-all-items textual_menu--section">
      <div class="page-link-group-copy">
        <div class="viewport" v-html="menu.menu_content_area_top"></div>
      </div>
    </div>

    <default-section v-for="sectionStructure in sectionStructures" :key="'section-' + sectionStructure.section.ms_id"
                     :menu="menu" :section="sectionStructure.section" :items="sectionStructure.items"
                     :ref="'section--' + sectionStructure.section.ms_id" :is-cart-enabled="isCartEnabled"></default-section>

    <div v-if="menu && menu.menu_content_area_bottom.length"
         class="body-container page-links no-images show-all-items textual_menu--section">
      <div class="page-link-group-copy">
        <div class="viewport" v-html="menu.menu_content_area_bottom"></div>
      </div>
    </div>

    <menu-section-list
      ref="footer"
      :current-section="currentSection"
      :sections="menu?.sections || []"
      v-on:update:currentSection="currentSection=$event"></menu-section-list>

    <peppermill-dialog v-if="isCartEnabled"
                       @close="showSelectedMenuItems = false" @submit="showSelectedMenuItems = false"
                       title="Saved Items" label-confirm="CLOSE"
                       :active="showSelectedMenuItems" is-alert>
      <ul slot="body" class="favorites--container">
        <li v-for="menuItem in favoriteMenuItems" :key="menuItem.mi_id">
          <div class="favorites--content">
            <h4>{{ menuItem.mi_title }}</h4>
            <h5>{{ menuItem.mi_subtitle }}</h5>
            <p>{{ menuItem.mi_description }}</p>
          </div>
          <a class="item_button--container item_button--container-view material-icons"
             @click.prevent.stop="scrollToMenuItem(menuItem.mi_id)" href="#"></a>
          <a class="item_button--container item_button--container-select material-icons"
             @click.prevent.stop="$store.commit('toggleMenuItemSelected', [menuItem.mi_id, menu?.menu_id || 0])" href="#"
             :class="{'item_button--container-active': -1 !== $store.state.selectedMenuItemIds.indexOf(menuItem.mi_id)}"
             :title="(-1 !== $store.state.selectedMenuItemIds.indexOf(menuItem.mi_id)) ? 'Click to remove this item from saved list' : 'Click to save this item for later'"></a>
        </li>
        <li v-if="!$store.state.selectedMenuItemIds.length">
          <div class="favorites--content">
            <i>No Favorites Selected.</i>
            <br>
            <br>
            Click the <span class="material-icons">favorite_border</span> next to a menu item you want to store as a
            favorite.
          </div>
        </li>
      </ul>
    </peppermill-dialog>

    <menu-schedules v-if="$store.state.isAdminView && menu" :normalized-schedules="menu?.normalizedSchedules" is-fixed></menu-schedules>

    <loader ref="loader" :active="isLoading" @click.native="isLoading = false"></loader>
  </div>
</template>

<script lang="ts">
/**
 * Controller for textual menu API.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.8.0
 * @copyright  Copyright (C) 2021-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2021-12-03 (1.1.0) (jhoward)
 *      - Add content monitoring functionality to make sure the user is seeing the latest content.
 *      - Break apart the data fetch method into multiple methods for more granular use.
 *
 *    2021-12-03 (1.1.1) (jhoward)
 *      - Add href attributes to all anchors.
 *      - Update .item cards to show the "favorite" button to the right of body content rather than underneath.
 *
 *    2022-01-07 (1.2.0) (jhoward)
 *      - Refactor menu section area to its own component (<menu-sections />).
 *
 *    2022-03-23 (1.3.0) (jhoward)
 *      - <textual-menu /> now includes menu.menu_content_area_top and menu.menu_content_area_bottom.
 *
 *    2022-04-21 (1.4.0) (jhoward)
 *      - Add support for menu and menu section version numbers in textual-menu.vue.
 *      - Hide horizontal scroll indicator on small UIs in textual-menu.vue when no horizontal scroll is available.
 *
 *    2022-04-21 (1.4.1) (jhoward)
 *      - <textual-menu> now waits for document.ready to gather cached data.
 *
 *    2024-06-10 (1.4.2) (jhoward)
 *      - Add descriptive titles to links in <textual-menu>.
 *
 *    2024-06-26 (1.5.0) (jhoward)
 *      - Add support for related menus in <textual-menu>.
 *      - Add <menu-switcher> to <textual-menu>.
 *      - Add descriptive title and aria-title attributes to <textual-menu>.
 *      - Export TextualMenuComponent interface in <textual-menu>.
 *      - Code improvements in <textual-menu>.
 *
 *    2024-07-10 (1.5.1) (jhoward)
 *      - Refactor "selected menu items" button to <textual-menu>.
 *
 *    2024-07-17 (1.6.0) (jhoward)
 *      - Allow HTML in item titles and prices in <textual-menu>.
 *      - Refactor TextualMenuComponent interface from <textual-menu> to menu defs file.
 *      - Add <textual-menu>::apiHostname prop to facilitate cross-hostname communication.
 *      - Add <textual-menu>::isConstantUpdate prop to facilitate loading data once at page load.
 *      - Add <textual-menu>::isForceLatestAssets prop to facilitate not reloading the page if assets are out of sync.
 *
 *    2024-07-18 (1.6.1) (jhoward)
 *      - Add a section subtitle output to <textual-menu>.
 *
 *    2024-08-19 (1.7.0) (jhoward)
 *      - Refactor <textual-menu> to use the new <default-section> component for textual menus.
 *      - Recalculate section offsets on window.load in <textual-menu>.
 *      - Add support for menu/section/item scheduling in <textual-menu>.
 *
 *    2024-08-26 (1.7.1) (jhoward)
 *      - Use store for selected items in <textual-menu>.
 *      - Fix scroll-to-section logic in <textual-menu>.
 *      - Refactor CSS to specific components from <textual-menu>.
 *
 *    2024-09-10 (1.7.2) (jhoward)
 *      - Fix small screen UI layout in <textual-menu>.
 *
 *    2024-10-23 (1.7.3) (jhoward)
 *      - Recalculate page link visibility after loading a new menu in <textual-menu>::switchToRelatedMenu().
 *
 *    2024-11-26 (1.7.4) (jhoward)
 *      - Load Google Material Icons from a local source in <textual-menu>.
 *
 *    2024-12-03 (1.7.5) (jhoward)
 *      - Add isCartEnabled data property and set it to false in <textual-menu>.
 *      - Only load the cart icon in <textual-menu> if isCartEnabled is true.
 *      - Only load the cart dialog in <textual-menu> if isCartEnabled is true.
 *      - Pass isCartEnabled data property to <search-menu> in <textual-menu>.
 *      - Pass isCartEnabled data property to <default-section> in <textual-menu>.
 *      - Horizontally scroll menu items into view if needed in <textual-menu>::scrollToMenuItem().
 *
 *    2024-12-03 (1.7.5) (jhoward)
 *      - Undo loading Google Material Icons from local source in <textual-menu>.
 *
 *    2025-02-12 (1.8.0) (jhoward)
 *      - Add <menu-schedules> in <textual-menu>.
 *      - Add `deviceRandomId` data property in <textual-menu>.
 *      - Set isAdminView in Vuex store in <textual-menu> mounted callback.
 *      - Read, generate if needed, and set a unique device id in <textual-menu> mounted callback.
 *      - Support textual_schedules in <textual-menu>::updateMenuData().
 *      - Support textual_schedules in <textual-menu>::normalizeMenuSchedules().
 */

import {fetchMenuData} from '../../menu-functions';
import SearchMenu, {SearchMenuComponent} from './search-menu.vue';
import MenuSectionList from "./menu-section-list.vue";
import MenuTag from "./menu-tag.vue";
import MenuTagLegend from "./menu-tag-legend.vue";
import MenuSwitcher from "./menu-switcher.vue";
import {
  Menu,
  MenuItem,
  SectionOffsetStructure, SectionStructure, TextualMenuComponent,
  UpdateMenuResponse
} from "./defs";
import {ComponentPublicInstance, defineComponent} from 'vue';
import {methodGetNormalizedSchedules, methodIsModelScheduled} from "../../helpers/menu-sections";
import DefaultSection from "./section/textual/defaultTextualSection.vue";
import generateUuid from "../../helpers/generateUuid";
import MenuSchedules from "./schedule/MenuSchedules.vue";

export default defineComponent({
  name: "textual-menu",
  components: {
    MenuSchedules,
    DefaultSection,
    MenuSwitcher,
    SearchMenu,
    MenuSectionList,
    MenuTag,
    MenuTagLegend
  },
  props: {
    menuId: {
      type: Number,
      default: 0
    },
    initialSection: {
      type: Number,
      default: -1
    },
    type: {
      type: String,
      default: 'TEXTUAL'
    },
    isAdminView: {
      type: Boolean,
      default: false
    },
    apiHostname: {
      type: String,
      required: true
    },
    isConstantUpdate: {
      type: Boolean,
      default: false
    },
    isForceLatestAssets: {
      type: Boolean,
      default: false
    },
    isCartEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentSection: this.initialSection,
      menu: null as Menu | null,
      relatedMenus: [] as Menu[],
      menuScrollPositions: {} as { [key: number]: number },
      deviceAssetCode: '',
      isDev: !!window.pepHelperFunctions.getParameterByName('test').length,
      isLoading: false,
      pageLinkObserver: null as IntersectionObserver | null,
      disableNavButtonScrolling: false,
      disableNavButtonScrollingTimer: -1,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      showSelectedMenuItems: false,
      abortController: new AbortController(),
      abortSignal: null,

      lastContentCheck: new Date(),
      contentCheckTimeout: 60000, //1 minute
      contentCheckTimer: -1,
      contentCheckDuration: 900000, //15 minutes
      contentHash: '',

      searchIndex: '',
      searchKey: '',
      searchHost: '',

      isUserAdmin: false,

      isDocumentLoaded: 'complete' === document.readyState,

      highlightTimeout: 7500,

      deviceRandomId: '',
    };
  },
  mounted() {
    this.$store.commit('setIsAdminView', this.isAdminView);

    const randomDeviceId = localStorage.getItem('random-device-id');
    if (randomDeviceId) {
      this.deviceRandomId = randomDeviceId;
    } else {
      this.deviceRandomId = generateUuid(24);
      localStorage.setItem('random-device-id', this.deviceRandomId);
    }

    this.initializeMenuData();
    $(this.getCachedData);

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
      this.isDocumentLoaded = true;
    }, {passive: true});

    //setup datetime interval
    this.$store.commit('updateDateTimer', window.setInterval(() => {
      this.$store.commit('updateDateTime', new Date());
    }, this.$store.state.dateTimeout));
  },
  watch: {

    currentSection() {
      const currentNavButtonNode: HTMLAnchorElement | null = this.$el.querySelector('#menu_nav--btn_' + this.currentSection);
      if (currentNavButtonNode) {
        const viewportNode = currentNavButtonNode.parentElement?.parentElement;
        if (viewportNode) {
          if (viewportNode.scrollLeft > currentNavButtonNode.offsetLeft) {
            viewportNode.scrollTo({
              behavior: 'smooth',
              left: currentNavButtonNode.offsetLeft - 12
            });
          } else if (currentNavButtonNode.offsetLeft + currentNavButtonNode.clientWidth > viewportNode.scrollLeft + viewportNode.clientWidth) {
            viewportNode.scrollTo({
              behavior: 'smooth',
              left: currentNavButtonNode.offsetLeft + currentNavButtonNode.clientWidth - viewportNode.clientWidth + 12
            });
          }
        }
      }
    },
  },
  computed: {
    currentMenuId(): number {
      if (this.menu?.menu_id) {
        return this.menu.menu_id;
      }

      return 0;
    },

    allRelatedMenus(): Menu[] {
      if (this.relatedMenus.length) {
        let output = [];
        if (this.menu && (this as TextualMenuComponent).isModelScheduled(this.menu?.normalizedSchedules || [])) {
          output.push(this.menu);
        }
        this.relatedMenus.forEach(menu => {
          if ((this as TextualMenuComponent).isModelScheduled(menu?.normalizedSchedules || [])) {
            output.push(menu);
          }
        });
        output.sort((a, b) => {
          return (a?.full_title || '').localeCompare(b?.full_title || '');
        });
        return output;
      }

      return [];
    },

    sectionOffsets(): SectionOffsetStructure[] {
      this.isDocumentLoaded;
      const output: SectionOffsetStructure[] = [];
      const parentOffsetTop = this.$el.parentElement?.offsetTop || 0;
      this.menu?.sections.forEach(section => {
        (this.$refs['section--' + section.ms_id] as ComponentPublicInstance[]).forEach(sectionComponent => {
          output.push({
            el: sectionComponent.$el as HTMLElement,
            id: section.ms_id,
            offsetTop: parentOffsetTop + (sectionComponent.$el as HTMLElement).offsetTop,
            hash: sectionComponent.$el.querySelector('.page-link-group-hash')?.id || ''
          });
        });
      });

      return output;
    },

    menuItems(): { [key: number]: MenuItem } {
      const output: { [key: number]: MenuItem } = {};
      this.menu?.sections.forEach(section => {
        if (section.menu_items) {
          section.menu_items.forEach(menuItem => {
            output[menuItem.mi_id] = menuItem;
          });
        }
      });
      return output;
    },

    favoriteMenuItems(): { [key: number]: MenuItem } {
      const output: { [key: number]: MenuItem } = {};
      for (let menuItemId in this.menuItems) {
        if (-1 !== this.$store.state.selectedMenuItemIds.indexOf(Number(menuItemId))) {
          output[menuItemId] = this.menuItems[menuItemId];
        }
      }
      return output;
    },

    sectionStructures(): SectionStructure[] {
      const output: SectionStructure[] = [];
      if (this.menu?.sections) {
        this.menu.sections.forEach(section => {
          output.push({
            section: section,
            items: (section.hasOwnProperty('ms_id')) ? this.getItemsForSection(section.ms_id) : []
          });
          if (section.children) {
            section.children.forEach(childSection => {
              output.push({
                section: childSection,
                items: (childSection.ms_id) ? this.getItemsForSection(childSection.ms_id) : []
              });
            });
          }
        });
      }

      return output;
    },
  },
  methods: {

    closeSearchMenu() {
      (this.$refs.search as ComponentPublicInstance | undefined)?.$el.dispatchEvent(new CustomEvent('close'))
    },

    isModelScheduled: methodIsModelScheduled,
    getNormalizedSchedules: methodGetNormalizedSchedules,

    switchToRelatedMenu(relatedMenuId: number) {
      if (this.menu && relatedMenuId !== this.menu.menu_id) {
        let newMainMenu = null;
        let newRelatedMenus: Menu[] = [];

        this.isLoading = true;

        this.relatedMenus.forEach(relatedMenu => {
          if (relatedMenuId === relatedMenu.menu_id) {
            newMainMenu = relatedMenu;
            if (this.menu) {
              newRelatedMenus.push(this.menu);
            }
          } else {
            newRelatedMenus.push(relatedMenu);
          }
        });

        this.relatedMenus = newRelatedMenus;
        this.menu = newMainMenu;
        (this.$refs.search as SearchMenuComponent | null)?.reset();

        window.setTimeout(() => {
          //scroll to last saved position
          this.isLoading = false;
          window.scrollTo(0, this.getMenuLastScrollPosition());

          window.setTimeout(() => {
            window.resetPageLinks();
            window.recalcPageLinks(true);
          }, 250);
        }, 500);
      }
    },

    getMenuLastScrollPosition() {
      if (this.menu && this.menuScrollPositions.hasOwnProperty(this.menu.menu_id)) {
        return this.menuScrollPositions[this.menu.menu_id];
      }

      return 0;
    },

    setupContentCheck() {
      clearTimeout(this.contentCheckTimer);
      this.contentCheckTimer = window.setTimeout(() => {
        if (this.lastContentCheck.getTime() < new Date().getTime() - this.contentCheckDuration) {
          //need to check for new data
          (this as TextualMenuComponent).getMenuData(this.menuId, this.contentHash, 'TEXTUAL', this.isDev, this.isForceLatestAssets)
            .then(this.updateMenuData)
            .then((contentUpdated) => {
              if (contentUpdated) {
                window.pepNotify.show(10000, 'Menu Updated');
              }
            })
            .finally(() => {
              //do it all over again
              this.setupContentCheck();
            });
        } else {
          this.setupContentCheck();
        }
      }, this.contentCheckTimeout);
    },

    getCachedData() {
      //look for cached selected items for this menu
      if (this.menu) {
        const cachedSelectedMenuItems = window.pepHelperFunctions.getCachedData('textual_menu--selectedMenuItems-' + this.menu.menu_id, 1000 * 60 * 60 * 24 * 90, false);
        if (cachedSelectedMenuItems) {
          if ('object' === typeof cachedSelectedMenuItems && cachedSelectedMenuItems.DATA && 'object' === typeof cachedSelectedMenuItems.DATA) {
            this.$store.commit('setSelectedMenuItemIds', cachedSelectedMenuItems.DATA);
          }
        }
      }
    },

    renderPageLinks(entries: IntersectionObserverEntry[]) {
      let visibleCount = 0;
      const localScrollReady = document.querySelector('html')?.classList.contains('scroll-ready');

      entries.forEach(function (entry) {
        const targetNode = entry.target as HTMLElement;
        if (targetNode.dataset.scrolled === 'YES') return;

        if (entry.intersectionRatio > 0.24 || (!localScrollReady && entry.intersectionRatio > 0)) {

          targetNode.classList.add('scrolled');
          targetNode.classList.add('delay-' + visibleCount++);

          targetNode.dataset.scrolled = 'YES';
        }

      });
    },

    calculateRootMarginTop() {
      return 0;//-1 * ((this.$el.querySelector('.body_container--section_nav')?.clientHeight || 0));
    },

    setupIntersectionObservers() {
      if (window.IntersectionObserver) {
        //only need to set up the observer once
        if (!this.pageLinkObserver) {
          this.pageLinkObserver = new IntersectionObserver(this.renderPageLinks, {
            root: null,	//defaults to viewport
            rootMargin: this.calculateRootMarginTop() + 'px 0px 0px 0px',
            threshold: [0, 0.1, 0.25]
          });
        }

        this.$el.querySelectorAll('.textual_menu--section .item').forEach(el => {
          this.pageLinkObserver?.observe(el);
        });
      }
    },

    updateMenuData(json: UpdateMenuResponse | null) {
      return new Promise(resolve => {
        if (json && json.STATUS === 'SUCCESS' && json.DATA.CONTENT_HASH) {

          //always update search metadata
          if (json.SETTINGS) {
            if (json.SETTINGS.SEARCH) {
              if (json.SETTINGS.SEARCH.INDEX) {
                this.searchIndex = json.SETTINGS.SEARCH.INDEX;
              }
              if (json.SETTINGS.SEARCH.KEY) {
                this.searchKey = json.SETTINGS.SEARCH.KEY;
              }
              if (json.SETTINGS.SEARCH.HOST) {
                this.searchHost = json.SETTINGS.SEARCH.HOST;
              }
            }
            if (json.SETTINGS.CONTENT_CHECK) {
              if (json.SETTINGS.CONTENT_CHECK.TIMEOUT) {
                this.contentCheckTimeout = json.SETTINGS.CONTENT_CHECK.TIMEOUT;
              }
              if (json.SETTINGS.CONTENT_CHECK.DURATION) {
                this.contentCheckDuration = json.SETTINGS.CONTENT_CHECK.DURATION;
              }
            }
            if (json.SETTINGS.USER_IS_ADMIN) {
              this.isUserAdmin = json.SETTINGS.USER_IS_ADMIN;
            }
          }

          if (this.contentHash !== json.DATA.CONTENT_HASH) {
            this.contentHash = json.DATA.CONTENT_HASH;

            if (json.DATA.MENU) {
              this.menu = this.normalizeMenuSchedules(json.DATA.MENU, (this.$store.state.isAdminView) ? 'schedules' : 'textual_schedules');
            }

            if (json.DATA.RELATED_MENUS) {
              this.relatedMenus = json.DATA.RELATED_MENUS.map((relatedMenu) => {
                return this.normalizeMenuSchedules(relatedMenu, (this.$store.state.isAdminView) ? 'schedules' : 'textual_schedules');
              });
            }

            if (json.DATA.DEVICE_ASSET_CODE) {
              this.deviceAssetCode = json.DATA.DEVICE_ASSET_CODE;
            }

            requestAnimationFrame(() => {
              this.setupIntersectionObservers();
            });
            resolve(true);
          } else {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      });
    },

    normalizeMenuSchedules(menu: Menu, scheduleType: 'schedules' | 'textual_schedules' | 'visual_schedules'): Menu {
      menu.normalizedSchedules = this.getNormalizedSchedules(menu[scheduleType] || []);
      menu.items_with_tags?.forEach(item => {
        item.normalizedSchedules = this.getNormalizedSchedules(item[scheduleType] || []);
      });

      menu.sections.map(section => {
        section.normalizedSchedules = this.getNormalizedSchedules(section[scheduleType] || []);

        section.menu_items?.forEach(item => {
          item.normalizedSchedules = this.getNormalizedSchedules(item[scheduleType] || []);
        });

        return section;
      });

      return menu;
    },

    initializeMenuData() {
      this.isLoading = true;

      (this as TextualMenuComponent).getMenuData(this.menuId, this.contentHash, 'TEXTUAL', this.isDev, this.isForceLatestAssets)
        .then(this.updateMenuData)
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.getCachedData();
          this.isLoading = false;
          if (this.isConstantUpdate) {
            this.setupContentCheck();
          }

          // if (this.sectionOffsets.length) {
          //   this.scrollToSection(this.sectionOffsets[0], false);
          // }
          window.scrollTo({
            behavior: 'smooth',
            top: 0
          });
        });
    },

    getMenuData: fetchMenuData,

    scrollToSection(sectionOffset: SectionOffsetStructure, isSmooth = true) {
      if (this.disableNavButtonScrolling) {
        return;
      }

      this.disableNavButtonScrolling = true;
      clearTimeout(this.disableNavButtonScrollingTimer);
      this.disableNavButtonScrollingTimer = window.setTimeout(() => {
        this.disableNavButtonScrolling = false;
      }, 1000);
      window.scrollTo({
        behavior: (isSmooth) ? 'smooth' : 'auto',
        top: sectionOffset.offsetTop
      });
      this.currentSection = sectionOffset.id;
    },

    scrollToMenuItem(menuItemId: number) {
      const menuItemNode: HTMLDivElement | null = document.querySelector('#textual_menu--item-' + menuItemId);
      if (menuItemNode) {
        window.scrollTo({
          behavior: 'smooth',
          top: menuItemNode.getBoundingClientRect().top + window.scrollY + this.calculateRootMarginTop() - 64
        });

        if (document.querySelector('html')?.classList.contains('ui-mobile')) {
          menuItemNode.closest('.viewport')?.scrollTo({ behavior: 'smooth', left: menuItemNode.offsetLeft});
        }

        menuItemNode.classList.add('item-highlight');
        window.setTimeout(() => {
          menuItemNode.classList.remove('item-highlight');
        }, this.highlightTimeout);
      }
      this.showSelectedMenuItems = false;
    },


    getItemsForSection(sectionId: number) {
      if (this.menu?.items_with_tags) {
        return this.menu.items_with_tags.filter(item => {
          return item.ms_id === sectionId;
        });
      }
      return [];
    },
  }
})
</script>

<style>
html {
  scroll-behavior: smooth;
}

html #header,
html #header-sticky {
  display: none;
}

#container {
  min-height: 100vh;
}

html:not(.ui-mobile) h1 {
  padding-left: 192px;
}
</style>

<style scoped>
.textual_menu--container:deep(.item_button--container) {
  position: relative;
  color: rgba(0, 0, 0, 0.54);
  font-size: 32px;
  width: 44px;
  height: 44px;
  line-height: 44px;
  text-align: center;
  text-decoration: none;
  float: right;
}

.textual_menu--container:deep(.item_button--container-cart) {
  position: fixed;
  right: 0;
  top: 3em;
  width: 48px;
  height: 48px;
  line-height: 48px;
  text-align: center;
  z-index: 10;
  text-decoration: none;
}

.textual_menu--container:deep(.item_button--container-active) {
  color: rgba(0, 103, 87, 0.54);
}

.textual_menu--container:deep(.item_button--container-view::before) {
  content: 'visibility';
}

.textual_menu--container:deep(.item_button--container-cart::before) {
  content: 'shopping_cart';
}

.textual_menu--container:deep(.item_button--container-select) {
  text-decoration: none;
}

.textual_menu--container:deep(.item_button--container-select::before) {
  content: 'favorite_border';
}

.textual_menu--container:deep(.item_button--container-select.item_button--container-active::before) {
  content: 'favorite';
}

.search--container {
  top: 4.5em;
}

.menu_switcher--container.menu_switcher--container-dock_left ~ .body-container.textual_menu--section.page-links {
  padding-left: calc(192px + 96px);
}

@media screen and (max-width: 800px) {
  .textual_menu--container {
    padding-top: 2em;
  }

  .menu_switcher--container.menu_switcher--container-dock_left ~ .body-container.textual_menu--section.page-links {
    padding-left: inherit;
  }
}

@media screen and (min-width: 801px) and (max-width: 1240px) {
  .textual_menu--container {
    padding-top: 2em;
  }

  .menu_switcher--container.menu_switcher--container-dock_left ~ .body-container.textual_menu--section.page-links {
    padding-left: calc(192px + 16px) !important;
  }
}

.favorites--container {
  list-style: none;
  font-size: 0.8rem;
  overflow: hidden;
  overflow-y: auto;
  padding: 0;
  position: relative;
  background-color: #ecefed;
  width: 100%;
}

.favorites--container h5,
.favorites--container p {
  margin-bottom: 0;
}

.favorites--container li {
  padding: 12px 32px;
  display: grid;
  grid-template-columns: 1fr 32px;
  grid-template-rows: 1fr 1fr;
  align-items: center;
  grid-gap: 8px;
}

.favorites--content {
  grid-row: 1 / -1;
  grid-column: 1;
}

.favorites--container li:nth-child(even) {
  background: #FFF;
}


.textual_menu--container:deep(.menu_version--container) {
  position: absolute;
  bottom: 1em;
  right: 1em;
}

.textual_menu--container:deep(.textual_menu--glass_effect) {
  background: rgba(255, 255, 255, 0.19);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  -webkit-backdrop-filter: blur(1em);
  backdrop-filter: blur(1em);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.textual_menu--container:deep(.search--container) {
  top: 0;
  position: fixed;
  left: 192px;
}

@media screen and (max-width: 800px) {
  .textual_menu--container:deep(.search--container) {
    left: 0;
  }
}
</style>